import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HandHeart, GraduationCap, Users, Heart, Stethoscope, BookOpen } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { useLanguage } from '../context/LanguageContext';
export function HomePage() {
  const {
    t,
    isRTL
  } = useLanguage();
  return <>
      <SEO title={t('nav.home')} description={t('home.hero.description')} data-id="element-407" />

      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white overflow-hidden min-h-[60vh] md:min-h-[65vh] flex items-center" data-id="element-408">
        <div className="absolute inset-0" data-id="element-409">
          <img src="https://cdn.magicpatterns.com/uploads/rgrSJawmhW3zF59H7uaMNf/Khabar_medical_college.jpg" alt="Khyber Medical College campus" className="h-full w-full object-cover object-center" data-id="element-410" />
          <div className="absolute inset-0 bg-black/25" data-id="element-411" />
        </div>

        <div className="relative container mx-auto px-4 md:px-6 py-12 md:py-16" data-id="element-412">
          <FadeIn className="max-w-4xl mx-auto" data-id="element-413">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-414">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-3 text-white" style={{
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
            }} data-id="element-415">
                {t('home.hero.title')}
                <br data-id="element-416" />
                {t('home.hero.subtitle')}
              </h1>
              <p className="text-base md:text-lg text-white/95 leading-relaxed" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
            }} data-id="element-417">
                {t('home.hero.description')}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Who We Are Section */}
      <Section background="white" data-id="element-418">
        <div className="max-w-4xl mx-auto" data-id="element-419">
          <FadeIn data-id="element-420">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-primary-900 text-center" data-id="element-421">
              {t('home.whoWeAre.title')}
            </h2>
            <div className="text-gray-600 leading-relaxed space-y-3 text-base md:text-lg text-left" data-id="element-422">
              <p data-id="element-423">
                The Khyber Medical Association UK (KMA-UK) is a professional and
                alumni organisation representing medical graduates and
                healthcare professionals of Khyber Pakhtunkhwa (KP) origin,
                primarily based in the United Kingdom and Ireland.
              </p>
              <p data-id="element-424">
                Rooted in the heritage of Khyber Medical College, KMA-UK is an
                inclusive association bringing together graduates from medical
                institutions across KP, Pakistan, and beyond. Our members are
                united by a shared commitment to excellence in healthcare,
                professionalism, ethical practice, and community service.
              </p>
              <p data-id="element-425">
                Founded by visionary doctors, KMA-UK provides a supportive
                platform for consultants, GPs, junior doctors, medical students,
                and allied health professionals. Through education,
                collaboration, and charitable initiatives, we strengthen
                professional networks and make a positive impact on healthcare.
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* What We Offer Section */}
      <Section background="gray" data-id="element-426">
        <div className="text-center max-w-3xl mx-auto mb-6" data-id="element-427">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-2 text-primary-900" data-id="element-428">
            {t('home.whatWeOffer.title')}
          </h2>
          <p className="text-gray-600" data-id="element-429">{t('home.whatWeOffer.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-430">
          <FadeIn delay={0} data-id="element-431">
            <Link to="/events" className="group" data-id="element-432">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-accent-200" data-id="element-433">
                <CardHeader className="text-center pb-3" data-id="element-434">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform" data-id="element-435">
                    <Users className="h-8 w-8" data-id="element-436" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-437">
                    {t('home.whatWeOffer.social.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-438">
                  <CardDescription className="text-base mb-3" data-id="element-439">
                    {t('home.whatWeOffer.social.description')}
                  </CardDescription>
                  <div className="text-accent-700 font-medium group-hover:underline inline-flex items-center" data-id="element-440">
                    {t('common.seeEvents')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-441" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-442">
            <Link to="/membership" className="group" data-id="element-443">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200" data-id="element-444">
                <CardHeader className="text-center pb-3" data-id="element-445">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform" data-id="element-446">
                    <GraduationCap className="h-8 w-8" data-id="element-447" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-448">
                    {t('home.whatWeOffer.membership.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-449">
                  <CardDescription className="text-base mb-3" data-id="element-450">
                    {t('home.whatWeOffer.membership.description')}
                  </CardDescription>
                  <div className="text-primary-700 font-medium group-hover:underline inline-flex items-center" data-id="element-451">
                    {t('common.learnMore')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-452" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-453">
            <Link to="/charity" className="group" data-id="element-454">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200" data-id="element-455">
                <CardHeader className="text-center pb-3" data-id="element-456">
                  <div className="h-16 w-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform" data-id="element-457">
                    <HandHeart className="h-8 w-8" data-id="element-458" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-459">
                    {t('home.whatWeOffer.charity.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-460">
                  <CardDescription className="text-base mb-3" data-id="element-461">
                    {t('home.whatWeOffer.charity.description')}
                  </CardDescription>
                  <div className="text-green-700 font-medium group-hover:underline inline-flex items-center" data-id="element-462">
                    {t('common.viewProjects')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-463" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>
        </div>
      </Section>

      {/* Our Latest Plans Section */}
      <Section data-id="element-464">
        <div className="text-center max-w-3xl mx-auto mb-6" data-id="element-465">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-2 text-primary-900" data-id="element-466">
            Our Latest Plans
          </h2>
          <p className="text-gray-600" data-id="element-467">
            Connect through social and cultural networks, access valuable
            membership benefits, and support impactful charity projects.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-468">
          <FadeIn delay={0} data-id="element-469">
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-470">
              <div className="h-48 overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center" data-id="element-471">
                <Heart className="h-20 w-20 text-blue-600 opacity-20" data-id="element-472" />
              </div>
              <CardContent className="flex-1 p-5" data-id="element-473">
                <h3 className="text-xl font-serif font-bold mb-2 text-primary-900" data-id="element-474">
                  Non-Communicable Diseases
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-475">
                  Developing programs to address chronic conditions and promote
                  preventive healthcare in underserved communities.
                </p>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-476">
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-477">
              <div className="h-48 overflow-hidden bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center" data-id="element-478">
                <Stethoscope className="h-20 w-20 text-red-600 opacity-20" data-id="element-479" />
              </div>
              <CardContent className="flex-1 p-5" data-id="element-480">
                <h3 className="text-xl font-serif font-bold mb-2 text-primary-900" data-id="element-481">
                  Trauma Medicine and Treatment
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-482">
                  Supporting emergency medical services and trauma care training
                  for healthcare professionals in Pakistan.
                </p>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-483">
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-484">
              <div className="h-48 overflow-hidden bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center" data-id="element-485">
                <BookOpen className="h-20 w-20 text-green-600 opacity-20" data-id="element-486" />
              </div>
              <CardContent className="flex-1 p-5" data-id="element-487">
                <h3 className="text-xl font-serif font-bold mb-2 text-primary-900" data-id="element-488">
                  Medical Education Fund
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-489">
                  Continuing our scholarship program to support deserving
                  medical students from Khyber Pakhtunkhwa.
                </p>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </Section>
    </>;
}